<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"></div>
    </div>

    <div class="relative max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="card">
        <div class="px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 class="text-xl font-semibold text-white font-display flex items-center gap-2">
              <svg class="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
              Аналитика
            </h1>
            <p class="text-gray-400 text-sm mt-1">Статистика и метрики ваших приложений</p>
          </div>
          
          <div class="flex items-center gap-3">
            <!-- Выбор приложения -->
            <select
              v-model="selectedApp"
              class="input-field text-sm py-2 min-w-[11rem]"
              @change="loadApplicationData"
            >
              <option :value="null">Все приложения</option>
              <option v-for="app in applications" :key="app.id" :value="app.id">
                {{ app.name }}
              </option>
            </select>
            
            <!-- Выбор периода -->
            <select
              v-model="selectedPeriod"
              class="input-field text-sm py-2"
              @change="loadData"
            >
              <option value="hour">Час</option>
              <option value="day">День</option>
              <option value="week">Неделя</option>
              <option value="month">Месяц</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- No Applications State -->
      <div v-else-if="applications.length === 0" class="card">
        <div class="px-6 py-12 text-center">
          <svg class="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <h3 class="text-lg font-medium text-white mb-2">Нет данных для отображения</h3>
          <p class="text-gray-400 mb-4">Создайте приложение, чтобы начать собирать статистику</p>
          <router-link to="/applications" class="btn-primary inline-flex items-center">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Создать приложение
          </router-link>
        </div>
      </div>

      <!-- Stats Cards -->
      <template v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Подключения -->
          <div class="card card-hover">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div class="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                  </svg>
                </div>
                <span class="text-xs text-gray-500 uppercase">Сейчас</span>
              </div>
              <div class="mt-4">
                <h3 class="text-2xl font-bold text-white font-display">
                  {{ totals.connections }}
                </h3>
                <p class="text-sm text-gray-400 mt-1">Подключений</p>
              </div>
              <div class="mt-3 flex items-center text-sm">
                <span class="text-gray-500">Пик: {{ peaks.connections }}</span>
              </div>
            </div>
          </div>

          <!-- Каналы -->
          <div class="card card-hover">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div class="w-12 h-12 bg-accent-yellow/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                  </svg>
                </div>
                <span class="text-xs text-gray-500 uppercase">Активных</span>
              </div>
              <div class="mt-4">
                <h3 class="text-2xl font-bold text-white font-display">
                  {{ totals.channels }}
                </h3>
                <p class="text-sm text-gray-400 mt-1">Каналов</p>
              </div>
              <div class="mt-3 flex items-center text-sm">
                <span class="text-gray-500">Пик: {{ peaks.channels }}</span>
              </div>
            </div>
          </div>

          <!-- Сообщения отправлено -->
          <div class="card card-hover">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div class="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                </div>
                <span class="text-xs text-gray-500 uppercase">Всего</span>
              </div>
              <div class="mt-4">
                <h3 class="text-2xl font-bold text-white font-display">
                  {{ formatNumber(totals.messages_sent) }}
                </h3>
                <p class="text-sm text-gray-400 mt-1">Сообщений отправлено</p>
              </div>
              <div class="mt-3 flex items-center text-sm">
                <span class="text-gray-500">{{ formatNumber(totals.messages_received) }} получено</span>
              </div>
            </div>
          </div>

          <!-- Скорость сообщений -->
          <div class="card card-hover">
            <div class="p-5">
              <div class="flex items-center justify-between">
                <div class="w-12 h-12 bg-accent-red/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
                <span class="text-xs text-gray-500 uppercase">Скорость</span>
              </div>
              <div class="mt-4">
                <h3 class="text-2xl font-bold text-white font-display">
                  {{ averages.messages_rate.toFixed(1) }}
                </h3>
                <p class="text-sm text-gray-400 mt-1">Сообщений/сек</p>
              </div>
              <div class="mt-3 flex items-center text-sm">
                <span class="text-gray-500">Пик: {{ peaks.messages_rate.toFixed(1) }}/сек</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Connections Chart -->
          <div class="card">
            <div class="px-6 py-4 border-b border-gray-800">
              <h3 class="text-lg font-semibold text-white font-display">Подключения</h3>
            </div>
            <div class="p-6">
              <div class="h-64 flex items-center justify-center">
                <canvas ref="connectionsChart"></canvas>
              </div>
            </div>
          </div>

          <!-- Messages Chart -->
          <div class="card">
            <div class="px-6 py-4 border-b border-gray-800">
              <h3 class="text-lg font-semibold text-white font-display">Сообщения (msg/sec)</h3>
            </div>
            <div class="p-6">
              <div class="h-64 flex items-center justify-center">
                <canvas ref="messagesChart"></canvas>
              </div>
            </div>
          </div>
        </div>

        <!-- Applications Summary Table (when viewing all) -->
        <div v-if="!selectedApp && summaryData?.applications" class="card">
          <div class="px-6 py-4 border-b border-gray-800">
            <h3 class="text-lg font-semibold text-white font-display">Статистика по приложениям</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-800">
              <thead class="bg-gray-900/50">
                <tr>
                  <th class="table-header">Приложение</th>
                  <th class="table-header text-right">Подключения</th>
                  <th class="table-header text-right">Каналы</th>
                  <th class="table-header text-right">Сообщения</th>
                  <th class="table-header text-right">Пик подключений</th>
                  <th class="table-header">Действия</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <tr
                  v-for="appSummary in summaryData.applications"
                  :key="appSummary.application.id"
                  class="table-row"
                >
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div class="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3">
                        <span class="text-primary-500 text-sm font-bold">
                          {{ appSummary.application.name.charAt(0).toUpperCase() }}
                        </span>
                      </div>
                      <div>
                        <div class="font-medium text-white">{{ appSummary.application.name }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell text-right text-white font-medium">
                    {{ appSummary.current.connections }}
                  </td>
                  <td class="table-cell text-right text-gray-400">
                    {{ appSummary.current.channels }}
                  </td>
                  <td class="table-cell text-right text-gray-400">
                    {{ formatNumber(appSummary.summary.total_messages_sent) }}
                  </td>
                  <td class="table-cell text-right text-gray-400">
                    {{ appSummary.summary.peak_connections }}
                  </td>
                  <td class="table-cell">
                    <button
                      @click="viewApplicationDetails(appSummary.application.id)"
                      class="text-primary-500 hover:text-primary-400 text-sm font-medium"
                    >
                      Подробнее →
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Last Update -->
        <div class="text-center text-gray-500 text-sm py-4">
          Последнее обновление: {{ lastUpdate }}
          <button @click="loadData" class="ml-2 text-primary-500 hover:text-primary-400">
            <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Обновить
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { useAnalyticsStore } from '../stores/analytics';
import { useApplicationsStore } from '../stores/applications';
import Chart from 'chart.js/auto';

export default {
  name: 'Analytics',
  setup() {
    const analyticsStore = useAnalyticsStore();
    const applicationsStore = useApplicationsStore();
    
    return {
      analyticsStore,
      applicationsStore,
    };
  },
  data() {
    return {
      selectedApp: null,
      selectedPeriod: 'day',
      lastUpdate: '',
      connectionsChartInstance: null,
      messagesChartInstance: null,
    };
  },
  computed: {
    loading() {
      return this.analyticsStore.isLoading || this.applicationsStore.isLoading;
    },
    applications() {
      return this.applicationsStore.applicationsList;
    },
    summaryData() {
      return this.analyticsStore.summary;
    },
    overviewData() {
      return this.analyticsStore.applicationOverview;
    },
    chartsData() {
      return this.analyticsStore.charts;
    },
    totals() {
      if (this.selectedApp && this.overviewData?.summary) {
        return {
          connections: this.overviewData.current?.connections ?? 0,
          channels: this.overviewData.current?.channels ?? 0,
          messages_sent: this.overviewData.summary.total_messages_sent ?? 0,
          messages_received: this.overviewData.summary.total_messages_received ?? 0,
        };
      }
      return this.summaryData?.totals || {
        connections: 0,
        channels: 0,
        messages_sent: 0,
        messages_received: 0,
      };
    },
    peaks() {
      if (this.selectedApp && this.overviewData?.peaks) {
        return this.overviewData.peaks;
      }
      // Для сводки вычисляем максимумы по всем приложениям
      if (this.summaryData?.applications) {
        return {
          connections: Math.max(...this.summaryData.applications.map(a => a.summary.peak_connections)),
          channels: Math.max(...this.summaryData.applications.map(a => a.summary.peak_channels)),
          messages_rate: 0,
        };
      }
      return { connections: 0, channels: 0, messages_rate: 0 };
    },
    averages() {
      if (this.selectedApp && this.overviewData?.averages) {
        return this.overviewData.averages;
      }
      return { connections: 0, channels: 0, messages_rate: 0 };
    },
  },
  async mounted() {
    await this.applicationsStore.fetchApplications();
    await this.loadData();
  },
  beforeUnmount() {
    this.destroyCharts();
  },
  methods: {
    async loadData() {
      this.lastUpdate = new Date().toLocaleTimeString('ru-RU');
      
      if (this.selectedApp) {
        await this.loadApplicationData();
      } else {
        await this.analyticsStore.fetchSummary(this.selectedPeriod);
      }
    },
    
    async loadApplicationData() {
      if (!this.selectedApp) {
        await this.analyticsStore.fetchSummary(this.selectedPeriod);
        this.destroyCharts();
        return;
      }
      
      await Promise.all([
        this.analyticsStore.fetchApplicationOverview(this.selectedApp, this.selectedPeriod),
        this.analyticsStore.fetchCharts(this.selectedApp, this.selectedPeriod),
      ]);
      
      this.$nextTick(() => {
        this.renderCharts();
      });
    },
    
    viewApplicationDetails(appId) {
      this.selectedApp = appId;
      this.loadApplicationData();
    },
    
    renderCharts() {
      this.destroyCharts();
      
      if (!this.chartsData?.charts) return;
      
      const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              color: 'rgba(255, 255, 255, 0.05)',
            },
            ticks: {
              color: '#6b7280',
              maxTicksLimit: 8,
            },
          },
          y: {
            grid: {
              color: 'rgba(255, 255, 255, 0.05)',
            },
            ticks: {
              color: '#6b7280',
            },
            beginAtZero: true,
          },
        },
      };
      
      // Connections Chart
      if (this.$refs.connectionsChart && this.chartsData.charts.connections) {
        const ctx = this.$refs.connectionsChart.getContext('2d');
        this.connectionsChartInstance = new Chart(ctx, {
          type: 'line',
          data: {
            labels: this.chartsData.charts.connections.labels,
            datasets: [{
              data: this.chartsData.charts.connections.values,
              borderColor: '#ff7f1f',
              backgroundColor: 'rgba(255, 127, 31, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 4,
            }],
          },
          options: chartOptions,
        });
      }
      
      // Messages Chart
      if (this.$refs.messagesChart && this.chartsData.charts.messages_rate) {
        const ctx = this.$refs.messagesChart.getContext('2d');
        this.messagesChartInstance = new Chart(ctx, {
          type: 'line',
          data: {
            labels: this.chartsData.charts.messages_rate.labels,
            datasets: [{
              data: this.chartsData.charts.messages_rate.values,
              borderColor: '#22c55e',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 4,
            }],
          },
          options: chartOptions,
        });
      }
    },
    
    destroyCharts() {
      if (this.connectionsChartInstance) {
        this.connectionsChartInstance.destroy();
        this.connectionsChartInstance = null;
      }
      if (this.messagesChartInstance) {
        this.messagesChartInstance.destroy();
        this.messagesChartInstance = null;
      }
    },
    
    formatNumber(num) {
      if (!num) return '0';
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    },
  },
};
</script>

